import { Box } from "@fideus-ui/core";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod/v4";
import { useQuery, useTRPC } from "../../../../../_app/api";
import {
  type CompanyDetailTab,
  TaxAdvisoryCompanyDetailsLayout,
} from "../../../../../companies/TaxAdvisoryCompanyDetailsLayout";

const searchSchema = z.object({
  detail: z.enum(["dokumente", "review", "offenlegung"]).optional(),
  jahr: z.coerce.number().optional(),
});

/** Used by TanStack Router generated routeTree.gen.ts for child route type inference @public */
export interface CompanySearchParams {
  detail?: CompanyDetailTab;
  jahr?: number;
}

export const Route = createFileRoute(
  "/beratung/$taxAdvisoryId/unternehmen/$companyId"
)({
  validateSearch: zodValidator(searchSchema),
  loader: ({
    context: { queryClient, trpc },
    params: { taxAdvisoryId, companyId },
  }) => {
    void queryClient.ensureQueryData(
      trpc.companies.getCompany.queryOptions({ taxAdvisoryId, companyId })
    );
    void queryClient.ensureQueryData(
      trpc.annualStatements.listAnnualStatementsByCompanyId.queryOptions({
        taxAdvisoryId,
        companyId,
      })
    );
  },
  component: CompanyDetailsRoute,
});

function CompanyDetailsRoute() {
  const { taxAdvisoryId, companyId } = Route.useParams();
  const { jahr } = Route.useSearch();
  const navigate = useNavigate();
  const trpc = useTRPC();

  const { data: company } = useQuery(
    trpc.companies.getCompany.queryOptions({ taxAdvisoryId, companyId })
  );

  const { data: annualStatements } = useQuery(
    trpc.annualStatements.listAnnualStatementsByCompanyId.queryOptions({
      taxAdvisoryId,
      companyId,
    })
  );

  const years = (annualStatements ?? [])
    .map((s) => s.year)
    .sort((a, b) => b - a);

  const onYearChange = (year: number | undefined) => {
    navigate({
      to: ".",
      search: (prev: CompanySearchParams) => ({
        ...prev,
        jahr: year,
      }),
    });
  };

  return (
    <Box
      component="main"
      sx={{
        paddingBottom: 1,
        bgcolor: "background.default",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TaxAdvisoryCompanyDetailsLayout
        taxAdvisoryId={taxAdvisoryId}
        companyId={companyId}
        companyName={company?.name}
        selectedYear={jahr}
        years={years}
        onYearChange={onYearChange}
      />
      <Outlet />
    </Box>
  );
}
